/**
 * SuggestedPrompts (P2.1-S6) — 4-card grid trong empty chat.
 *
 * Click card → set composer draft (qua chat store) → Composer auto-fill
 * + focus textarea.
 *
 * Context-aware: admin role thấy thêm card "Tạo workflow" thay slot #4.
 */
import * as React from "react";

import { useAuthStore } from "@/stores/auth";
import { useChatStore } from "@/stores/chat";

interface Suggestion {
  emoji: string;
  title: string;
  hint: string;
  prompt: string;
}

const PROMPTS_BASE: Suggestion[] = [
  {
    emoji: "💰",
    title: "Tạo báo giá",
    hint: '"Báo giá Cty Phú An 3 màn hình"',
    prompt: "Tạo báo giá cho Cty Phú An 3 màn hình 27 inch",
  },
  {
    emoji: "📦",
    title: "Xem báo giá",
    hint: '"5 báo giá draft gần nhất"',
    prompt: "Cho em xem 5 báo giá draft gần nhất",
  },
  {
    emoji: "🔍",
    title: "Tìm khách hàng",
    hint: '"Tìm Cty Phú An"',
    prompt: "Tìm khách hàng tên Phú An",
  },
  {
    emoji: "📊",
    title: "Doanh số",
    hint: '"Doanh số tháng này"',
    prompt: "Cho em xem doanh số tháng này",
  },
];

const PROMPT_ADMIN: Suggestion = {
  emoji: "🛠",
  title: "Tạo workflow",
  hint: '"Tạo workflow phê duyệt PR"',
  prompt: "Hướng dẫn em tạo workflow phê duyệt PR step-by-step",
};

export function SuggestedPrompts(): JSX.Element {
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);
  const roles = useAuthStore((s) => s.user?.roles ?? []);
  const isAdmin = roles.includes("admin");

  // Admin sees workflow card thay vì doanh số (last slot)
  const items = React.useMemo<Suggestion[]>(() => {
    if (!isAdmin) return PROMPTS_BASE;
    return [...PROMPTS_BASE.slice(0, 3), PROMPT_ADMIN];
  }, [isAdmin]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-2xl mx-auto">
      {items.map((s) => (
        <button
          key={s.title}
          type="button"
          onClick={() => setComposerDraft(s.prompt)}
          className="rounded-lg border bg-card p-4 text-left hover:border-primary/50 hover:shadow-card hover:bg-gradient-brand-soft active:scale-[0.98] transition-all"
        >
          <div className="text-base">{s.emoji}</div>
          <div className="text-sm font-medium mt-2">{s.title}</div>
          <div className="text-xs text-muted-foreground italic mt-1 line-clamp-2">
            {s.hint}
          </div>
        </button>
      ))}
    </div>
  );
}
